import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { adminApi } from "@/lib/api";
import {
  BookOpen, Users, Brain, Inbox, FileText, Newspaper, ArrowRight,
  Image as ImageIcon, Sparkles, Award, Globe2, Compass, Menu, ScrollText, FormInput,
} from "lucide-react";

const TILES = [
  { col: "page_content", label: "Pages", icon: FileText, blurb: "Home, Alliance, Manifesto, Research…" },
  { col: "books", label: "Books", icon: BookOpen, blurb: "Beyond Science volumes" },
  { col: "founder", label: "Founder Profile", icon: Users, blurb: "Bio, portraits, links", singleton: true },
  { col: "founder_recognitions", label: "Founder Recognition", icon: Award, blurb: "Awards & media proof" },
  { col: "founding_members", label: "Founding Members", icon: Users, blurb: "Council & advisors" },
  { col: "research_domains", label: "Research Domains", icon: Brain, blurb: "Top-level research areas" },
  { col: "research_questions", label: "Research Questions", icon: Compass, blurb: "Compendium entries" },
  { col: "programs", label: "Programs", icon: Globe2, blurb: "Initiatives, summits, fellowships" },
  { col: "media_items", label: "Media / Insights", icon: Newspaper, blurb: "Talks, press, podcasts" },
  { col: "legal_pages", label: "Legal Pages", icon: ScrollText, blurb: "Privacy, Terms" },
  { col: "site_settings", label: "Site Settings", icon: Sparkles, blurb: "Identity, contact, brand", singleton: true },
  { col: "navigation", label: "Navigation / Menu", icon: Menu, blurb: "Header & CTA links", singleton: true },
  { col: "form_configs", label: "Engage Forms", icon: FormInput, blurb: "Labels, fields, success text", singleton: true },
];

export default function AdminDashboard() {
  const [counts, setCounts] = useState({});
  const [submissions, setSubmissions] = useState(0);
  const [assetsCount, setAssetsCount] = useState(0);
  const [unreviewed, setUnreviewed] = useState(0);

  useEffect(() => {
    document.title = "BSCA Admin · Dashboard";
    TILES.forEach(({ col }) => {
      adminApi
        .list(col)
        .then((r) => setCounts((p) => ({ ...p, [col]: r.length })))
        .catch(() => {});
    });
    adminApi.submissions().then((s) => {
      setSubmissions(s.length);
      setUnreviewed(s.filter((r) => !r.reviewed).length);
    }).catch(() => {});
    adminApi.assetsList().then((a) => setAssetsCount(a.length)).catch(() => {});
  }, []);

  return (
    <div className="p-8 md:p-12">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
        <div>
          <div className="bsca-eyebrow text-[#7A5A06]">Welcome</div>
          <h1 className="font-serif text-4xl md:text-5xl tracking-tight">Content Console</h1>
          <p className="mt-2 text-[#4B5563]">Manage every editable surface of bscalliance.org — no code required.</p>
        </div>
        <div className="flex items-center gap-3">
          <Link to="/admin/submissions" data-testid="link-submissions" className="bsca-lift bg-[#050B14] text-white px-5 py-3 inline-flex items-center text-sm rounded-sm">
            <Inbox className="h-4 w-4 mr-2" />
            {submissions} submissions
            {unreviewed > 0 && <span className="ml-2 inline-flex items-center justify-center bg-[#D4AF37] text-[#050B14] text-[10px] font-medium rounded-full h-5 px-2">{unreviewed} new</span>}
            <ArrowRight className="h-4 w-4 ml-2" />
          </Link>
          <Link to="/admin/assets" data-testid="link-assets" className="bsca-lift bg-[#D4AF37] text-[#050B14] px-5 py-3 inline-flex items-center text-sm rounded-sm">
            <ImageIcon className="h-4 w-4 mr-2" /> {assetsCount} assets <ArrowRight className="h-4 w-4 ml-2" />
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {TILES.map((c) => (
          <Link
            key={c.col}
            to={`/admin/c/${c.col}`}
            data-testid={`admin-tile-${c.col}`}
            className="bg-white border border-black/10 p-6 hover:border-[#D4AF37]/60 transition-colors group"
          >
            <c.icon className="h-5 w-5 text-[#D4AF37]" />
            <h3 className="mt-4 font-serif text-xl">{c.label}</h3>
            <div className="mt-1 text-xs text-[#4B5563]">{c.blurb}</div>
            <div className="mt-4 flex items-center justify-between">
              <span className="text-xs text-[#9CA3AF]">
                {c.singleton ? "Single profile" : `${counts[c.col] ?? "—"} entries`}
              </span>
              <span className="inline-flex items-center text-xs bsca-link">Manage <ArrowRight className="h-3 w-3 ml-1" /></span>
            </div>
          </Link>
        ))}
      </div>

      <div className="mt-10 grid md:grid-cols-2 gap-6">
        <div className="bg-white border border-black/10 p-6">
          <div className="bsca-eyebrow text-[#7A5A06]">Editing tips</div>
          <ul className="mt-3 text-sm text-[#4B5563] space-y-1.5">
            <li>Every module opens a friendly form. No code or JSON needed.</li>
            <li>Images come from the central <b>Assets / Media Library</b> — upload once, use anywhere.</li>
            <li>Toggle <b>Show / Hide</b> on any entry to control public visibility.</li>
            <li>Need raw access? Tick <b>Advanced (developer JSON)</b> inside any editor.</li>
          </ul>
        </div>
        <div className="bg-white border border-black/10 p-6">
          <div className="bsca-eyebrow text-[#7A5A06]">Status meanings</div>
          <ul className="mt-3 text-sm text-[#4B5563] space-y-1.5">
            <li><b>Published</b> — visible on the public site</li>
            <li><b>Draft</b> — hidden from the public site</li>
            <li><b>Placeholder</b> — visible with a &ldquo;CMS placeholder&rdquo; badge</li>
            <li><b>In review / Coming soon / Planned</b> — visible with a status badge</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
